import type Tippy from '@tippyjs/react';
import type { TippyProps } from '@tippyjs/react';
import { type ForwardedRef, forwardRef, useCallback, useMemo } from 'react';

import { useBoolState } from '@didaskill/ui/utils';

const DEFAULT_PLUGINS = [];

export type WithLazyTippyProps = Omit<TippyProps, 'ref'>;

// A tippy component that mounts its content only when visible.
// This is useful if there are state initialization in the tippy content.
export const withLazyTippy = (WrappedTippy: typeof Tippy) => {
  const WithLazyTippy = forwardRef(
    (
      { plugins = DEFAULT_PLUGINS, render, content, ...props }: WithLazyTippyProps,
      ref: ForwardedRef<HTMLElement>,
    ) => {
      const computedProps: WithLazyTippyProps = { ...props };

      const [isMounted, onMount, onUnmount] = useBoolState();

      const lazyPlugin = useMemo(
        () => ({
          fn: () => ({
            onShow: onMount,
            onHidden: onUnmount,
          }),
        }),
        [onMount, onUnmount],
      );

      computedProps.plugins = useMemo(
        () => [lazyPlugin, ...(plugins || [])],
        [lazyPlugin, plugins],
      );

      const renderIfMounted = useCallback(
        (...args: Parameters<WithLazyTippyProps['render']>) => (isMounted ? render(...args) : ''),
        [isMounted, render],
      );

      if (render) {
        computedProps.render = renderIfMounted;
      } else {
        computedProps.content = isMounted ? content : '';
      }

      return <WrappedTippy {...computedProps} ref={ref} />;
    },
  );

  WithLazyTippy.displayName = 'WithLazyTippy';

  return WithLazyTippy;
};
